import React, { forwardRef, useContext } from "react";
import { Link } from "react-router-dom";

import { cn } from "src/shadcn/lib/utils";
import { renderIcon, type IconProp } from "src/shared/renderIcon";
import {
  Tabs as PrimitiveTabs,
  TabsList as PrimitiveTabsList,
  TabsTrigger as PrimitiveTabsTrigger,
  TabsContent as PrimitiveTabsContent,
} from "src/primitives/Tabs";

import { SIZE_CLASSES, LIST_HEIGHT, TabsSizeContext } from "./constants";

type LinkTo = React.ComponentProps<typeof Link>["to"];

type TabsSize = "default" | "lg";

export interface TabsProps extends React.ComponentProps<typeof PrimitiveTabs> {
  /** Size of tab triggers. Applied to all Tabs.Item children. */
  size?: TabsSize;
}

type TabsListProps = React.ComponentProps<typeof PrimitiveTabsList>;

interface TabsItemProps extends React.ComponentProps<
  typeof PrimitiveTabsTrigger
> {
  /** Icon component, element, or class-name string rendered before the label. */
  icon?: IconProp;
  /** When set, the trigger renders as a react-router-dom Link to this path. */
  to?: LinkTo;
}

type TabsContentProps = React.ComponentProps<typeof PrimitiveTabsContent>;

const TabsRoot = forwardRef<HTMLDivElement, TabsProps>(
  ({ size = "default", children, ...props }, ref) => (
    <TabsSizeContext.Provider value={size}>
      <PrimitiveTabs ref={ref} {...props}>
        {children}
      </PrimitiveTabs>
    </TabsSizeContext.Provider>
  )
);
TabsRoot.displayName = "Tabs";

const TabsListComponent = forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, ...props }, ref) => {
    const size = useContext(TabsSizeContext);

    return (
      <PrimitiveTabsList
        ref={ref}
        className={cn(LIST_HEIGHT[size], className)}
        {...props}
      />
    );
  }
);
TabsListComponent.displayName = "Tabs.List";

const TabsItemComponent = forwardRef<HTMLButtonElement, TabsItemProps>(
  ({ icon, className, children, to, ...otherProps }, ref) => {
    const size = useContext(TabsSizeContext);
    const content = icon ? (
      <>
        {renderIcon(icon, size === "lg" ? "size-5" : "size-4")}
        {children}
      </>
    ) : (
      children
    );

    if (to !== undefined) {
      return (
        <PrimitiveTabsTrigger
          asChild
          ref={ref}
          className={cn(SIZE_CLASSES[size], icon && "gap-1.5", className)}
          {...otherProps}
        >
          <Link to={to}>{content}</Link>
        </PrimitiveTabsTrigger>
      );
    }

    return (
      <PrimitiveTabsTrigger
        ref={ref}
        className={cn(SIZE_CLASSES[size], icon && "gap-1.5", className)}
        {...otherProps}
      >
        {content}
      </PrimitiveTabsTrigger>
    );
  }
);
TabsItemComponent.displayName = "Tabs.Item";

const TabsContentComponent = forwardRef<HTMLDivElement, TabsContentProps>(
  (props, ref) => <PrimitiveTabsContent ref={ref} {...props} />
);
TabsContentComponent.displayName = "Tabs.Content";

const Tabs = Object.assign(TabsRoot, {
  List: TabsListComponent,
  Item: TabsItemComponent,
  Content: TabsContentComponent,
});

export { Tabs };
